import React from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReplyIcon from '@mui/icons-material/Reply';

import { DialogCloseButton, SlideTransition } from '@components';
import { useCloseOnBack } from '@hooks';
import { toggle } from '@utils';
import { countBadgeSx } from 'theme/badges';
import { buttonSizeSx, secondaryButtonSx } from 'theme/buttons';
import { touchTargetSx } from 'theme/touchTarget';
import { ICandidatesControls } from '../controls';
import { CandidateFilterSections } from './CandidateFilterSections';
import { CandidateSearch } from './CandidateSearch';
import { GroupByMenu } from './GroupByMenu';
import { PositionList } from './PositionFilterChips';
import { ShowRejectedToggle } from './ShowRejectedToggle';

export const CandidateFiltersDialog: React.FC<ICandidatesControls> = ({
  data,
  filterOptions,
  view,
  activeFilterCount,
  isDefault,
  inactiveCount,
  onChange,
  onReset,
}) => {
  const { open, openOnTop, close } = useCloseOnBack('candidateFilters');

  return (
    <>
      <IconButton
        aria-label={
          activeFilterCount ? `Filters, ${activeFilterCount} active` : 'Filters'
        }
        onClick={openOnTop}
        sx={{
          display: { tablet: 'none' },
          ml: 'auto',
          color: 'text.secondary',
          ...touchTargetSx,
        }}
      >
        <Badge
          badgeContent={activeFilterCount}
          sx={{ '& .MuiBadge-badge': countBadgeSx }}
        >
          <FilterListIcon />
        </Badge>
      </IconButton>

      <Dialog
        fullScreen
        open={open}
        onClose={close}
        TransitionComponent={SlideTransition}
      >
        <Stack direction="row" alignItems="center" gap={1.5} p={2}>
          <DialogTitle variant="h3" sx={{ p: 0, flex: 1 }}>
            Candidates for your positions
          </DialogTitle>
          <DialogCloseButton onClick={close} />
        </Stack>

        <DialogContent sx={{ px: 2, py: 0, overscrollBehavior: 'contain' }}>
          <Stack gap={2}>
            <CandidateSearch
              fullWidth
              data={data}
              value={view.query}
              onChange={(query) => onChange({ query })}
            />

            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{
                '& .MuiSelect-select': { fontSize: '0.875rem' },
                '& .MuiFormControlLabel-root': { mr: 0 },
              }}
            >
              <GroupByMenu
                value={view.groupBy}
                onChange={(groupBy) => onChange({ groupBy })}
              />
              <ShowRejectedToggle
                checked={view.showRejected}
                count={inactiveCount}
                onChange={(showRejected) => onChange({ showRejected })}
              />
            </Stack>

            <CandidateFilterSections
              {...filterOptions}
              selectedRegions={view.regions}
              selectedSeniorities={view.seniorities}
              selectedStatuses={view.statuses}
              onChange={onChange}
            />

            <PositionList
              positions={data}
              selectedIds={view.positionIds}
              onToggle={(id) =>
                onChange({ positionIds: toggle(view.positionIds, id) })
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ReplyIcon />}
            disabled={isDefault}
            onClick={onReset}
            sx={{ ...buttonSizeSx.md, ...secondaryButtonSx }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
